import { z } from 'zod'

import { EnumBuilder } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { CountSchema } from './property.models.ts'
import {
  ProjectIdSchema,
  ProjectServiceIdSchema,
  ReviewIdSchema,
  ReviewUrnSchema,
} from './unique-id.models.ts'

/**
 * ReviewType
 */
export const [ReviewTypes, ReviewTypeSchema, ReviewTypeEnum] = EnumBuilder(
  'pre_production',
  'post_production'
)
export type ReviewType = z.infer<typeof ReviewTypeSchema>

/**
 * ReviewKey
 */
export const PreProductionReviewKeySchema = z.object({
  reviewType: ReviewTypeSchema.extract(['pre_production']),
  projectId: ProjectIdSchema,
  projectServiceId: ProjectServiceIdSchema,
})

export type PreProductionReviewKey = z.infer<
  typeof PreProductionReviewKeySchema
>

export const PostProductionReviewKeySchema = z.object({
  reviewType: ReviewTypeSchema.extract(['post_production']),
  projectId: ProjectIdSchema,
})

export type PostProductionReviewKey = z.infer<
  typeof PostProductionReviewKeySchema
>

export const ReviewKeySchema = z.discriminatedUnion('reviewType', [
  PreProductionReviewKeySchema,
  PostProductionReviewKeySchema,
])

export type ReviewKey = z.infer<typeof ReviewKeySchema>

/**
 * ReviewRoundCount
 */
export const ReviewRoundCountSchema = CountSchema
export type ReviewRoundCount = z.infer<typeof ReviewRoundCountSchema>

/**
 * ReviewRoundNumber
 */
export const ReviewRoundNumberSchema = z.number().int().positive()
export type ReviewRoundNumber = z.infer<typeof ReviewRoundNumberSchema>

/**
 * ReviewStatus
 */
export const [ReviewStatuses, ReviewStatusSchema, ReviewStatusEnum] =
  EnumBuilder('concepting', 'reviewing', 'resolving', 'resolved')
export type ReviewStatus = z.infer<typeof ReviewStatusSchema>

/**
 * ReviewDomainSelectModel
 */
export const ReviewDomainSelectModelSchema = z.object({
  reviewId: ReviewIdSchema,
  reviewUrn: ReviewUrnSchema,
  reviewKey: ReviewKeySchema,
  maxChangeRequests: ReviewRoundCountSchema,
  currentRoundNumber: ReviewRoundNumberSchema,
  status: ReviewStatusSchema,
})

export type ReviewDomainSelectModel = z.infer<
  typeof ReviewDomainSelectModelSchema
>

/**
 * ReviewDomainInsertModel
 */
export const ReviewDomainInsertModelSchema = ReviewDomainSelectModelSchema.omit(
  {
    reviewUrn: true,
    status: true, // Always inserted as concepting
    currentRoundNumber: true, // Always inserted as 1
  }
).partial({
  reviewId: true,
})

export type ReviewDomainInsertModel = z.infer<
  typeof ReviewDomainInsertModelSchema
>

/**
 * ReviewDomainUpdateModel
 */
export const ReviewDomainUpdateModelSchema = ReviewDomainSelectModelSchema.pick(
  {
    maxChangeRequests: true,
    currentRoundNumber: true,
    status: true,
  }
).partial()

export type ReviewDomainUpdateModel = z.infer<
  typeof ReviewDomainUpdateModelSchema
>

/**
 * ReviewDomainSelectModelFilter
 */
export const ReviewDomainSelectModelFiltersSchema = z
  .object({
    reviewId: ModelFilterSchema(ReviewIdSchema),
    reviewUrn: ModelFilterSchema(ReviewUrnSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    projectServiceId: ModelFilterSchema(ProjectServiceIdSchema),
    reviewType: ModelFilterSchema(ReviewTypeSchema),
    status: ModelFilterSchema(ReviewStatusSchema),
  })
  .partial()

export type ReviewDomainSelectModelFilters = z.infer<
  typeof ReviewDomainSelectModelFiltersSchema
>
